import { Observable, combineLatest, map } from "rxjs";
import { DBFactory } from "~/classes/database/db_factory";
import { Classroom } from "~/classes/models/classrooms/classroom.model";
import { AssignmentsService } from "../assignments/assignments.service";
import { ClassSessionsService } from "../class-sessions/class-sessions.service";
import {
  AssignmentResourceTemplate,
  AssignmentResourceTemplates,
} from "./assignment-resource-template.model";
import { AssignmentTemplate } from "./assignment-template.model";
import { ClassSessionTemplate } from "./class-session-template.model";
import {
  ClassroomTemplate,
  ClassroomTemplates,
} from "./classroom-template.model";

export class ClassroomTemplatesService {
  static streamUserClassroomTemplates({
    userId,
    organizationIds,
  }: {
    userId: string;
    organizationIds?: string[];
  }): Observable<ClassroomTemplates> {
    const db = DBFactory.createDatabase();

    const userStream = db
      .streamList(
        {
          collection: `/classroomTemplates`,
          path: `/classroomTemplates`,
        },
        [
          {
            type: "where",
            field: "userId",
            operator: "==",
            value: userId,
          },
          {
            type: "orderBy",
            field: "name",
            direction: "asc",
          },
        ]
      )
      .pipe(
        map((data) => {
          return data
            .map((classroomTemplate: any) => {
              return ClassroomTemplate.fromMap(classroomTemplate);
            })
            .filter((classroomTemplate) => {
              return classroomTemplate.organizationId == undefined;
            });
        })
      );

    if (organizationIds != undefined && organizationIds.length > 0) {
      const organizationStreams = db
        .streamList(
          {
            collection: `/classroomTemplates`,
            path: `/classroomTemplates`,
          },
          [
            {
              type: "where",
              field: "organizationId",
              value: organizationIds,
              operator: "in",
            },
            {
              type: "orderBy",
              field: "name",
              direction: "asc",
            },
          ]
        )
        .pipe(
          map((data) => {
            return data.map((classroomTemplate: any) => {
              return ClassroomTemplate.fromMap(classroomTemplate);
            });
          })
        );

      return combineLatest([userStream, organizationStreams]).pipe(
        map(([userTemplates, organizationTemplates]) => {
          return [...userTemplates, ...organizationTemplates].sort((a, b) =>
            a.label.localeCompare(b.label)
          );
        })
      );
    }

    return userStream;
  }

  static streamOrganizationClassroomTemplates({
    organizationId,
  }: {
    organizationId: string;
  }): Observable<ClassroomTemplates> {
    const db = DBFactory.createDatabase();

    return db
      .streamList(
        {
          collection: `/classroomTemplates`,
          path: `/classroomTemplates`,
        },
        [
          {
            type: "where",
            field: "organizationId",
            value: organizationId,
            operator: "==",
          },
          {
            type: "orderBy",
            field: "name",
            direction: "asc",
          },
        ]
      )
      .pipe(
        map((data) => {
          return data.map((classroomTemplate: any) => {
            return ClassroomTemplate.fromMap(classroomTemplate);
          });
        })
      );
  }

  static async fetchUserClassroomTemplates({ userId }: { userId: string }) {
    const db = DBFactory.createDatabase();
    const data = await db.list(
      {
        collection: `/classroomTemplates`,
        path: `/classroomTemplates`,
      },
      [
        {
          type: "where",
          field: "userId",
          value: userId,
          operator: "==",
        },
      ]
    );

    return data.map((classroomTemplate: any) => {
      return ClassroomTemplate.fromMap(classroomTemplate);
    });
  }

  static async fetchClassroomTemplate({
    classroomTemplateId,
  }: {
    classroomTemplateId: string;
  }) {
    const db = DBFactory.createDatabase();
    const classroomTemplate = await db.get({
      collection: `/classroomTemplates`,
      path: `/classroomTemplates/${classroomTemplateId}`,
    });

    return ClassroomTemplate.fromMap(classroomTemplate) as ClassroomTemplate;
  }

  static async fetchClassroomTemplateAssignments({
    userId,
    organizationId,
    classroomTemplateId,
  }: {
    userId: string;
    organizationId?: string;
    classroomTemplateId: string;
  }) {
    const db = DBFactory.createDatabase();

    let query = [
      {
        type: "where",
        field: "userId",
        value: userId,
        operator: "==",
      },
    ] as ModelQueryConfig;

    if (organizationId) {
      query = [
        {
          type: "where",
          field: "organizationId",
          value: organizationId,
          operator: "==",
        },
      ];
    }

    const data = await db.list(
      {
        collection: `/classroomTemplates/${classroomTemplateId}/assignments`,
        path: `/classroomTemplates/${classroomTemplateId}/assignments`,
      },
      query
    );

    return data.map((assignmentTemplate: any) => {
      return AssignmentTemplate.fromMap(assignmentTemplate);
    });
  }

  static async fetchClassroomTemplateAssignmentResources({
    userId,
    organizationId,
    classroomTemplateId,
  }: {
    userId: string;
    organizationId?: string;
    classroomTemplateId: string;
  }) {
    const db = DBFactory.createDatabase();

    const query = [
      {
        type: "where",
        field: "classroomId",
        value: classroomTemplateId,
        operator: "==",
      },
    ] as ModelQueryConfig;

    if (organizationId) {
      query.push({
        type: "where",
        field: "organizationId",
        value: organizationId,
        operator: "==",
      });
    } else {
      query.push({
        type: "where",
        field: "userId",
        value: userId,
        operator: "==",
      });
    }

    const data = await db.list(
      {
        collectionGroup: "resources",
        collection: "resources",
        path: "resources",
      },
      query
    );

    return data.map((assignmentResourceTemplate: any) => {
      return AssignmentResourceTemplate.fromMap(assignmentResourceTemplate);
    });
  }

  static async fetchAssignmentTemplateResources({
    userId,
    classroomTemplateId,
    assignmentTemplateId,
    organizationId,
  }: {
    userId: string;
    classroomTemplateId: string;
    assignmentTemplateId: string;
    organizationId?: string;
  }) {
    const db = DBFactory.createDatabase();

    let query = [
      {
        type: "where",
        field: "userId",
        value: userId,
        operator: "==",
      },
    ] as ModelQueryConfig;

    if (organizationId) {
      query = [
        {
          type: "where",
          field: "organizationId",
          value: organizationId,
          operator: "==",
        },
      ];
    }

    const data = await db.list(
      {
        collection: `/classroomTemplates/${classroomTemplateId}/assignments/${assignmentTemplateId}/resources`,
        path: `/classroomTemplates/${classroomTemplateId}/assignments/${assignmentTemplateId}/resources`,
      },
      query
    );

    return data.map((assignmentResourceTemplate: any) => {
      return AssignmentResourceTemplate.fromMap(assignmentResourceTemplate);
    });
  }

  static async fetchClassroomClassSessionTemplates({
    userId,
    organizationId,
    classroomTemplateId,
  }: {
    userId: string;
    organizationId?: string;
    classroomTemplateId: string;
  }) {
    const db = DBFactory.createDatabase();

    let query = [
      {
        type: "where",
        field: "userId",
        value: userId,
        operator: "==",
      },
    ] as ModelQueryConfig;

    if (organizationId) {
      query = [
        {
          type: "where",
          field: "organizationId",
          value: organizationId,
          operator: "==",
        },
      ];
    }

    const data = await db.list(
      {
        collection: `/classroomTemplates/${classroomTemplateId}/classSessions`,
        path: `/classroomTemplates/${classroomTemplateId}/classSessions`,
      },
      query
    );

    return data.map((classSessionTemplate: any) => {
      return ClassSessionTemplate.fromMap(classSessionTemplate);
    });
  }

  static async saveClassroomAsTemplate({
    classroom,
    templateName,
  }: {
    classroom: Classroom;
    templateName: string;
  }) {
    const classroomTemplate = ClassroomTemplate.fromClassroom(classroom);
    classroomTemplate.name = templateName;
    classroomTemplate.label = templateName;
    await classroomTemplate.save();

    if (!classroom.id || !classroomTemplate.id) {
      throw new Error("Classroom or Classroom template Id is missing");
    }

    const assignments = await AssignmentsService.listAssignments(
      classroom.id!,
      classroom.userId
    );

    const activeAssignments = assignments
      .filter((assignment) => !assignment.isArchived)
      .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));

    const mapAssignmentIdToTemplateId: { [key: string]: string } = {};

    const assignmentTemplates = activeAssignments.map((assignment, index) => {
      assignment.displayOrder = index;

      const assignmentTemplate = AssignmentTemplate.fromAssignment({
        assignment,
        classroomTemplateId: classroomTemplate.id!,
      });

      assignmentTemplate.organizationId = classroomTemplate.organizationId;

      mapAssignmentIdToTemplateId[assignment.id!] = assignmentTemplate.id!;

      return assignmentTemplate;
    });

    const classSessions =
      await ClassSessionsService.fetchClassroomClassSessions({
        userId: classroom.userId,
        classroomId: classroom.id!,
      });

    const activeClassSessions = classSessions.filter(
      (classSession) => classSession.isArchived != true
    );

    const classSerssionTempaltes = activeClassSessions.map(
      (classSession, index) => {
        return ClassSessionTemplate.fromClassSession({
          classSession,
          classroomTemplateId: classroomTemplate.id!,
          displayOrder: index,
        });
      }
    );

    const assignmentResources =
      await AssignmentsService.fetchAllClassroomAssignmentResources({
        userId: classroom.userId,
        classroomId: classroom.id!,
      });

    const assignmentResourceTemplates = [] as AssignmentResourceTemplates;
    for (const assignmentResource of assignmentResources) {
      if (
        mapAssignmentIdToTemplateId[assignmentResource.assignmentId] ==
        undefined
      )
        continue;

      const assignmentResourceTemplate =
        AssignmentResourceTemplate.fromAssignmentResource({
          assignmentResource,
          classroomTemplateId: classroomTemplate.id!,
          assignmentTemplateId:
            mapAssignmentIdToTemplateId[assignmentResource.assignmentId],
        });

      assignmentResourceTemplates.push(assignmentResourceTemplate);
    }

    const db = DBFactory.createDatabase();
    await db.batchUpdate([
      ...assignmentTemplates,
      ...classSerssionTempaltes,
      ...assignmentResourceTemplates,
    ]);

    for (const group of classroomTemplate.classroomAssignmentGroups ?? []) {
      group.assignmentIds = group.assignmentIds.map((assignmentId: string) => {
        return mapAssignmentIdToTemplateId[assignmentId];
      });
    }

    await classroomTemplate.save();

    await ClassroomTemplatesService.copyClassroomTemplateResources({
      classroomTemplateId: classroomTemplate.id!,
    });
  }

  static async copyClassroomTemplateResources({
    classroomTemplateId,
  }: {
    classroomTemplateId: string;
  }) {
    if (process.server) return;

    await $fetch(
      `/api/lms/classrooms/template/${classroomTemplateId}/copy-resources`,
      {
        method: "GET",
        headers: await useApiHeaders(),
      }
    );
  }

  static async fetchFullClassroomTemplate({
    classroomTemplateId,
  }: {
    classroomTemplateId: string;
  }) {
    const classroomTemplate =
      await ClassroomTemplatesService.fetchClassroomTemplate({
        classroomTemplateId,
      });

    const assignmentTemplateIdsToAssignmentIds: { [key: string]: string } = {};

    const assignmentTemplates =
      await ClassroomTemplatesService.fetchClassroomTemplateAssignments({
        userId: classroomTemplate.userId,
        organizationId: classroomTemplate.organizationId,
        classroomTemplateId: classroomTemplate.id!,
      });

    assignmentTemplates.sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );

    const classSessionTemplates =
      await ClassroomTemplatesService.fetchClassroomClassSessionTemplates({
        userId: classroomTemplate.userId,
        organizationId: classroomTemplate.organizationId,
        classroomTemplateId: classroomTemplate.id!,
      });

    classSessionTemplates.sort(
      (a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
    );

    const assignmentResourceTemplates =
      await ClassroomTemplatesService.fetchClassroomTemplateAssignmentResources(
        {
          userId: classroomTemplate.userId,
          organizationId: classroomTemplate.organizationId,
          classroomTemplateId: classroomTemplate.id!,
        }
      );

    return {
      classroomTemplate,
      assignmentTemplates,
      classSessionTemplates,
      assignmentResourceTemplates,
      assignmentTemplateIdsToAssignmentIds,
    };
  }

  static async createClassroomFromTemplate({
    classroomTemplate,
    userId,
  }: {
    userId: string;
    classroomTemplate: ClassroomTemplate;
  }) {
    const classroom = classroomTemplate.toClassroom();
    classroom.userId = userId;

    const {
      assignmentTemplates,
      classSessionTemplates,
      assignmentResourceTemplates,
      assignmentTemplateIdsToAssignmentIds,
    } = await ClassroomTemplatesService.fetchFullClassroomTemplate({
      classroomTemplateId: classroomTemplate.id!,
    });

    const assignments = assignmentTemplates.map((assignmentTemplate) => {
      const assignment = assignmentTemplate.toAssignment(classroom.id!);
      assignmentTemplateIdsToAssignmentIds[assignmentTemplate.id!] =
        assignment.id!;
      assignment.userId = userId;
      return assignment;
    });

    const classSessions = classSessionTemplates.map((classSessionTemplate) => {
      var session = classSessionTemplate.toClassSession({
        classroomId: classroom.id!,
      });

      session.userId = userId;

      return session;
    });

    const assignmentResources = assignmentResourceTemplates.map(
      (assignmentResourceTemplate) => {
        var resource = assignmentResourceTemplate.toAssignmentResource({
          classroomId: classroom.id!,
          assignmentId:
            assignmentTemplateIdsToAssignmentIds[
              assignmentResourceTemplate.assignmentId
            ],
        });

        resource.userId = userId;

        return resource;
      }
    );

    return {
      classroom,
      assignments,
      assignmentResources,
      classSessions,
    };
  }
}
